import React, { useState, useEffect, useRef } from 'react';
import ModalLayer from './ModalLayer';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';
import { resetAppScroll } from '../utils/appScroll';
import { AGENT_MOBILE_TABS } from '../shared/mobileTabs';
import AppTabBar from './AppTabBar';
import Dashboard from './Dashboard';
import Profile from './Profile';
import ClientManagement from './ClientManagement';
import Integrations from './Integrations';
import { getApiUrl, isWebApp } from '../utils/platform';
import VendorManagement from './VendorManagement';
import TaskLibrary from './TaskLibrary';
import ProjectManager from './ProjectManager';
import TeamManagement from './TeamManagement';
import { showsTeamScreen } from '../shared/teamSeats';
import TeamDashboard from './TeamDashboard';
import Settings from './Settings';
import AnnouncementBanner from './AnnouncementBanner';
import OnboardingTour from './OnboardingTour';
import HamburgerMenu from './HamburgerMenu';
import { formatDateOnly, isBeforeDateOnly, localDateString } from '../utils/dates';
import { applyTaskCompletion, mergeLoadedTasks } from '../utils/taskListState';
import { readAgentLink } from '../shared/agentLinks';
import { PUSH_OPEN_EVENT } from '../utils/pushNavigation';
import { APP_REFRESH_EVENT } from '../utils/appRefresh';
import { agentMayMarkTaskDone, clientLabel, groupTasksByClient, helpTaskIdSet, taskNeedsAttention } from '../shared/agentTaskView';
import TapToExpand from './TapToExpand';
import { displayTaskDescription } from '../shared/homeRegion';
import { chatHistoryPayload } from '../shared/chatHistory';
import { latestApplianceLabel } from '../shared/applianceLabel';
import { messagesInContext, mergeOlder, rowsToMessages } from '../shared/assistantThread';
import { assistantErrorMessage, messagesBeforeSend, readAssistantJson } from '../shared/assistantTurn';
import { postAssistant } from '../shared/timedFetch';
import { noteAssistantFailure } from '../shared/clientErrorReport';
import { prepareAssistantPhoto } from '../utils/taskPhotoFile';
import AssistantThread from './AssistantThread';
import { showToast } from './Toast';
import QuestionsLeftNote from './QuestionsLeftNote';
import { monthlyQuestionLimit } from '../shared/aiQuota';
import { AgentAiNotice } from './AiAssistantNotice';
import HomeHistoryReportAgent from './HomeHistoryReportAgent';

const AdminDashboard = ({ profile, onLogout, onProfileUpdate }) => {
  const { colors, isDark, toggleTheme } = useTheme();
  const linked = readAgentLink(window.location.search);
  const refreshAgentRef = useRef(() => {});

  const [activeSection, setActiveSection] = useState(
    linked.section === 'tasks' || linked.section === 'settings' ? linked.section : 'dashboard'
  );
  const [tourReplay, setTourReplay] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeUsers: 0,
    totalVendors: 0,
    referrals: 0
  });

  // AI Assistant state
  const [chatMessages, setChatMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadLoadingOlder, setThreadLoadingOlder] = useState(false);
  const [threadHasOlder, setThreadHasOlder] = useState(false);
  const [threadCursor, setThreadCursor] = useState(null);
  const threadRequested = useRef(false);
  const [userInput, setUserInput] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [questionUsage, setQuestionUsage] = useState({ used: 0, limit: monthlyQuestionLimit(process.env) });
  const [changePlanRequest, setChangePlanRequest] = useState(0);
  const [changePlanPreset, setChangePlanPreset] = useState(null);

  const [moreListToken, setMoreListToken] = useState(0);
  const openAgentSection = (section, options) => {
    if (options && options.openChangePlan) {
      setChangePlanPreset(options.plan || null);
      setChangePlanRequest((count) => count + 1);
    }
    setActiveSection(section);
  };
  const selectSection = (id) => {
    if (id === 'settings') setMoreListToken((count) => count + 1);
    setActiveSection(id);
  };

  // Client Tasks state
  const [clientTasks, setClientTasks] = useState([]);
  const [helpRequests, setHelpRequests] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [filteredClientId, setFilteredClientId] = useState(linked.clientId || 'all');
  const [taskStatusFilter, setTaskStatusFilter] = useState(linked.taskId ? 'all' : 'attention');
  const [visibleTaskCount, setVisibleTaskCount] = useState(25);
  const [focusTaskId, setFocusTaskId] = useState(linked.taskId || '');
  const linkedTaskId = focusTaskId;
  const [expandedClientTaskId, setExpandedClientTaskId] = useState(linked.taskId || null);
  const [taskViewMode, setTaskViewMode] = useState('client'); // 'client' or 'library'
  const [showAddClientTaskModal, setShowAddClientTaskModal] = useState(false);
  const [newClientTaskData, setNewClientTaskData] = useState({
    client_id: '',
    title: '',
    description: '',
    due_date: '',
    category: 'general'
  });
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    resetAppScroll();
  }, [activeSection]);

  useEffect(() => {
    const onPush = (event) => {
      const detail = (event && event.detail) || {};
      if (detail.screen === 'projects') {
        setActiveSection('projects');
        return;
      }
      if (detail.screen !== 'tasks') return;
      setActiveSection('tasks');
      setTaskViewMode('client');
      setTaskStatusFilter('all');
      if (detail.clientId) setFilteredClientId(detail.clientId);
      if (detail.taskId) {
        setFocusTaskId(detail.taskId);
        setExpandedClientTaskId(detail.taskId);
      }
    };
    window.addEventListener(PUSH_OPEN_EVENT, onPush);
    const onRefresh = () => { refreshAgentRef.current(); };
    window.addEventListener(APP_REFRESH_EVENT, onRefresh);
    const onLink = () => {
      const next = readAgentLink(window.location.search);
      if (next.section === 'tasks' || next.section === 'settings') setActiveSection(next.section);
      if (next.clientId) setFilteredClientId(next.clientId);
      if (next.taskId) {
        setFocusTaskId(next.taskId);
        setExpandedClientTaskId(next.taskId);
        setTaskStatusFilter('all');
      }
    };
    window.addEventListener('popstate', onLink);
    return () => {
      window.removeEventListener(PUSH_OPEN_EVENT, onPush);
      window.removeEventListener(APP_REFRESH_EVENT, onRefresh);
      window.removeEventListener('popstate', onLink);
    };
  }, []);

  useEffect(() => {
    setVisibleTaskCount(25);
  }, [filteredClientId, taskStatusFilter]);

  useEffect(() => {
    if (!linkedTaskId || activeSection !== 'tasks') return undefined;
    const matches = (task) => {
      if (filteredClientId !== 'all' && task.client_id !== filteredClientId) return false;
      if (taskStatusFilter === 'pending' && task.completed) return false;
      if (taskStatusFilter === 'completed' && !task.completed) return false;
      return true;
    };
    const index = clientTasks.filter(matches).findIndex((task) => task.id === linkedTaskId);
    if (index >= visibleTaskCount) setVisibleTaskCount(index + 1);
    const node = document.getElementById(`client-task-${linkedTaskId}`);
    if (node) node.scrollIntoView({ block: 'center' });
    return undefined;
  }, [linkedTaskId, activeSection, clientTasks, filteredClientId, taskStatusFilter, visibleTaskCount]);

  useEffect(() => {
    loadStats();
    loadVendors();
    loadClientTasks();
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const agentThread = async (body) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session && session.access_token;
    if (!token) return null;
    const response = await fetch(getApiUrl('/.netlify/functions/assistant-thread'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(payload.error || 'Could not load your chat.');
      error.status = response.status;
      throw error;
    }
    return payload;
  };

  const applyAgentThread = (result, mode) => {
    const next = rowsToMessages((result && result.data) || []);
    setThreadHasOlder(Boolean(result && result.hasOlder));
    setThreadCursor((result && result.nextBefore) || null);
    setChatMessages((current) => {
      if (mode === 'older') return mergeOlder(current, next);
      const local = current.filter((msg) => !msg.rowId);
      return next.concat(local);
    });
    return next;
  };

  useEffect(() => {
    if (activeSection !== 'assistant') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const response = await fetch(getApiUrl('/.netlify/functions/ai-usage'), {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const body = await response.json().catch(() => ({}));
        if (!cancelled && response.ok && body && body.limit) setQuestionUsage(body);
        if (cancelled || threadRequested.current) return;
        threadRequested.current = true;
        setThreadLoading(true);
        try {
          const page = await agentThread({ action: 'page' });
          if (!cancelled && page) applyAgentThread(page, 'replace');
        } catch (threadError) {
          threadRequested.current = false;
          console.error('Error loading chat:', threadError);
        } finally {
          if (!cancelled) setThreadLoading(false);
        }
      } catch (error) {
        console.error('Error loading assistant usage:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSection]);

  const loadOlderAgentChat = async () => {
    if (!threadHasOlder || threadLoadingOlder || !threadCursor) return;
    setThreadLoadingOlder(true);
    try {
      const result = await agentThread({
        action: 'page',
        before: threadCursor.createdAt,
        beforeId: threadCursor.id,
      });
      if (result) applyAgentThread(result, 'older');
    } catch (error) {
      console.error('Error loading older chat:', error);
    } finally {
      setThreadLoadingOlder(false);
    }
  };

  const searchAgentChat = async (query) => {
    const result = await agentThread({ action: 'search', query });
    return (result && result.matches) || [];
  };

  const revealAgentChat = async (hit) => {
    const messageId = hit && (hit.messageId || `${hit.id}:assistant`);
    if (!messageId || !threadHasOlder || !threadCursor) return;
    let cursor = threadCursor;
    let more = threadHasOlder;
    let guard = 0;
    while (more && cursor && guard < 20) {
      guard += 1;
      const result = await agentThread({
        action: 'page',
        before: cursor.createdAt,
        beforeId: cursor.id,
      });
      if (!result) return;
      const next = applyAgentThread(result, 'older');
      if (next.some((msg) => msg.id === messageId)) return;
      more = Boolean(result.hasOlder);
      cursor = result.nextBefore || null;
      if (!next.length) return;
    }
  };

  const handleAgentStartFresh = async () => {
    const marker = {
      id: `local-fresh-${Date.now()}`,
      fresh: true,
      role: 'fresh',
      timestamp: new Date(),
    };
    setChatMessages((prev) => prev.concat(marker));
    try {
      await agentThread({ action: 'fresh' });
    } catch (error) {
      setChatMessages((prev) => prev.filter((msg) => msg.id !== marker.id));
      showToast('Could not start fresh. Try again.');
    }
  };

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle image selection - Sharp backend handles all format conversions (including HEIC)
  const handleImageSelect = (file) => {
    if (!file) {
      setSelectedImage(null);
      setImagePreviewUrl(null);
      return;
    }

    // Set the selected image (Sharp will handle HEIC conversion on backend)
    setSelectedImage(file);

    // Create preview URL using FileReader (more reliable on iOS than createObjectURL)
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const loadStats = async () => {
    if (!profile?.id) return;

    try {
      const [clientsResult, vendorsResult, referralsResult] = await Promise.all([
        supabase.from('clients').select('id, status').eq('agent_id', profile.id),
        supabase.from('vendors').select('id').eq('agent_id', profile.id),
        supabase.from('referrals').select('id').eq('agent_id', profile.id),
      ]);
      const clients = clientsResult.data;
      const vendors = vendorsResult.data;
      const referrals = referralsResult.data;

      setStats({
        totalClients: clients?.length || 0,
        activeUsers: clients?.filter(c => c.status === 'active').length || 0,
        totalVendors: vendors?.length || 0,
        referrals: referrals?.length || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadVendors = async () => {
    if (!profile?.id) return;
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('agent_id', profile.id);
      if (error) throw error;
      setVendors(data || []);
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

    const loadClientTasks = async (change) => {
    if (!profile?.id) return;
    try {
      const [clientsResult, tasksResult, helpResult] = await Promise.all([
        supabase
          .from('clients')
          .select('id, first_name, last_name')
          .eq('agent_id', profile.id),
        supabase
          .from('client_tasks')
          .select(`
            *,
            clients!inner(id, first_name, last_name, address, state, region_code),
            task:tasks(id, title, description, frequency, category)
          `)
          .eq('clients.agent_id', profile.id)
          .order('due_date', { ascending: true }),
        supabase
          .from('task_help_requests')
          .select('id, client_id, client_task_id, task_title, message, status, created_at, clients!inner(agent_id)')
          .eq('clients.agent_id', profile.id)
          .order('created_at', { ascending: false }),
      ]);

      if (clientsResult.error) throw clientsResult.error;
      const clients = clientsResult.data || [];
      setAllClients(clients);
      if (clients.length === 0) {
        setClientTasks([]);
        setHelpRequests([]);
        return;
      }
      if (tasksResult.error) throw tasksResult.error;
      setClientTasks((current) => mergeLoadedTasks(current, tasksResult.data || [], change));
      if (helpResult.error) console.error('Error loading help requests:', helpResult.error);
      else {
        setHelpRequests((helpResult.data || []).map((row) => {
          const next = Object.assign({}, row);
          delete next.clients;
          return next;
        }));
      }
    } catch (error) {
      console.error('Error loading client tasks:', error);
    }
  };

  const handleAddClientTask = async () => {
    if (!newClientTaskData.client_id) {
      showToast('Please select a client.');
      return;
    }
    if (!newClientTaskData.title.trim()) {
      showToast('Please enter a task title.');
      return;
    }
    if (!newClientTaskData.due_date) {
      showToast('Please select a due date.');
      return;
    }

    try {
      const { error } = await supabase
        .from('client_tasks')
        .insert([{
          client_id: newClientTaskData.client_id,
          task_name: newClientTaskData.title.trim(),
          description: newClientTaskData.description.trim() || null,
          due_date: newClientTaskData.due_date,
          category: newClientTaskData.category || null,
          completed: false
        }]);

      if (error) throw error;

      await loadClientTasks();
      setShowAddClientTaskModal(false);
      setNewClientTaskData({
        client_id: '',
        title: '',
        description: '',
        due_date: '',
        category: 'general'
      });
      showToast('Task added successfully!');
    } catch (error) {
      console.error('Error adding task:', error);
      showToast('Error adding task. Please try again.');
    }
  };

  const handleToggleClientTask = async (taskId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const completedAt = newStatus ? new Date().toISOString() : null;
      const { error } = await supabase
        .from('client_tasks')
        .update({
          completed: newStatus,
          completed_at: completedAt
        })
        .eq('id', taskId);

      if (error) throw error;
      const change = { taskId, completed: newStatus, completedAt, removeIds: [], addTasks: [] };
      setClientTasks((current) => applyTaskCompletion(current, change));
      const source = clientTasks.find((task) => task.id === taskId);
      if (source && source.client_id) {
        supabase.rpc('pacarion_topup_client', { p_client_id: source.client_id }).then((topped) => {
          if (topped.error && !/pacarion_topup_client|schema cache|does not exist/i.test(topped.error.message || '')) {
            console.error('Task top-up failed', topped.error);
          }
        }).catch((topupError) => {
          console.error('Task top-up failed', topupError);
        });
      }
      await loadClientTasks(change);
    } catch (error) {
      console.error('Error toggling task:', error);
      showToast('Error updating task. Please try again.');
    }
  };

  const handleSendMessage = async (retryMessage) => {
    const retry = retryMessage && retryMessage.error ? retryMessage : null;
    const imageFile = retry ? retry.retryImage : selectedImage;
    const imageUrl = retry ? (retry.retryImageUrl || '') : imagePreviewUrl;
    if (!retry && !userInput.trim() && !imageFile) {
      showToast('Please enter a message or select an image');
      return;
    }

    const userMessage = retry
      ? retry.retryQuestion
      : (userInput.trim() || (imageFile ? 'Uploaded an image' : ''));
    const priorMessages = messagesInContext(messagesBeforeSend(chatMessages, retry));

    const showFailure = (where, detail) => {
      noteAssistantFailure(where, detail);
      const status = Number(String(detail || '').split(' ')[0]) || 0;
      const serverError = String(detail || '').replace(/^\d+\s*/, '');
      setChatMessages((prev) => prev
        .filter((msg) => !retry || msg.id !== retry.id)
        .concat(assistantErrorMessage({
          where,
          status,
          serverError,
          network: !status,
          question: userMessage,
          image: imageFile,
          imageUrl,
        })));
    };

    if (!retry) {
      setChatMessages((prev) => [...prev, {
        role: 'user',
        content: userMessage,
        image: imageFile && imageFile.name,
        imageUrl,
        timestamp: new Date(),
      }]);
      setUserInput('');
    } else {
      setChatMessages((prev) => prev.filter((msg) => msg.id !== retry.id));
    }

    setAiLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        showFailure('before-send', '401 not signed in');
        return;
      }
      const accessToken = session.access_token;
      const conversationHistory = chatHistoryPayload(priorMessages);
      const label = latestApplianceLabel(priorMessages);
      let response;
      let where = 'ask-question';

      if (imageFile) {
        where = 'analyze-appliance';
        const formData = new FormData();
        formData.append('image', await prepareAssistantPhoto(imageFile));
        formData.append('userMessage', userMessage || 'Please identify this appliance and provide maintenance information.');
        formData.append('conversationHistory', JSON.stringify(conversationHistory));
        response = await postAssistant(getApiUrl('/.netlify/functions/analyze-appliance'), {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
          body: formData,
        });
      } else {
        response = await postAssistant(getApiUrl('/.netlify/functions/ask-question'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            question: userMessage,
            vendors,
            clientInfo: { property_type: 'single_family', features: [] },
            conversationHistory,
            label,
          }),
        });
      }

      const payload = await readAssistantJson(response);
      const result = payload.body || {};
      if (result.monthlyUsage) setQuestionUsage(result.monthlyUsage);
      const answer = imageFile ? result.analysis : result.answer;
      if (!payload.ok || result.error || !answer) {
        const detail = result.details ? ` ${result.details}` : '';
        showFailure(where, `${payload.status || 0} ${result.error || 'no answer'}${detail}`);
        return;
      }

      setChatMessages((prev) => [...prev, {
        role: 'assistant',
        content: answer,
        label: result.label || null,
        timestamp: new Date(),
      }]);
      if (imageFile) handleImageSelect(null);
    } catch (error) {
      showFailure(imageFile ? 'analyze-appliance' : 'ask-question', error && error.message ? error.message : 'network');
    } finally {
      setAiLoading(false);
    }
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'profile', label: 'My Profile' },
    { id: 'clients', label: 'Clients' },
    { id: 'vendors', label: 'Vendors' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'projects', label: 'Projects' },
    ...(showsTeamScreen(profile) ? [{ id: 'team', label: 'Team' }] : []),
    { id: 'assistant', label: 'Assistant' },
    ...(isWebApp() ? [{ id: 'integrations', label: 'Integrations' }] : []),
    { id: 'home-history', label: 'Pacarion Home History Report' },
    { id: 'settings', label: 'More' }
  ];

  refreshAgentRef.current = () => { loadClientTasks(); };

  return (
    <div className={`app-shell${activeSection === 'assistant' ? ' assistant-open' : ''}`} style={{...styles.container, background: colors.background}}>
      <AnnouncementBanner audience="agent" colors={colors} />
      <OnboardingTour
        profile={profile}
        replaySignal={tourReplay}
        onShowStep={(step) => {
          if (step && step.section) setActiveSection(step.section);
        }}
      />

      {/* iOS Bottom Tab Bar - Mobile Only */}
      {isMobile && (
        <AppTabBar
          tabs={AGENT_MOBILE_TABS}
          activeId={activeSection}
          onSelect={selectSection}
          colors={colors}
          isDark={isDark}
        />
      )}

      <header
        className={isMobile ? 'mobile-top-bar' : undefined}
        style={{
        ...styles.header,
        background: colors.surface,
        color: colors.text,
        borderBottom: `1px solid ${colors.border}`,
        boxSizing: 'border-box',
        width: isMobile ? '100%' : undefined,
        maxWidth: '100%',
        padding: isMobile ? undefined : '1.5rem 3rem',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : undefined,
        paddingBottom: isMobile ? 0 : undefined,
        paddingLeft: isMobile ? 'calc(env(safe-area-inset-left, 0px) + 16px)' : undefined,
        paddingRight: isMobile ? 'calc(env(safe-area-inset-right, 0px) + 16px)' : undefined,
        minHeight: isMobile ? 'calc(env(safe-area-inset-top, 0px) + var(--mobile-top-bar-height, 44px))' : undefined,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        position: isMobile ? 'fixed' : 'relative',
        top: isMobile ? 0 : 'auto',
        left: isMobile ? 0 : 'auto',
        right: isMobile ? 0 : 'auto',
        zIndex: isMobile ? 999 : 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'space-between',
        transform: 'none'
      }}>
        <img
          className="top-bar-wordmark"
          src={isDark ? `${process.env.PUBLIC_URL}/images/logo/pacarion-logo-dark.png` : `${process.env.PUBLIC_URL}/images/logo/pacarion-logo-light.png`}
          alt="Pacarion"
          style={{
            height: isMobile ? '33px' : '40px',
            width: 'auto',
            maxWidth: '70%'
          }}
          onError={(e) => e.target.style.display = 'none'}
        />
        {/* Desktop-only header buttons */}
        {!isMobile && (
          <div style={styles.headerRight}>
            <span style={{...styles.agentName, color: colors.textSecondary}}>
              {profile?.first_name} {profile?.last_name}
            </span>
            <button style={{...styles.logoutBtn, color: colors.text, borderColor: colors.borderLight}} onClick={onLogout}>
              Logout
            </button>
          </div>
        )}
      </header>

      <div className="app-shell-body" style={styles.main}>
        {/* Desktop-only Sidebar Navigation */}
        {!isMobile && (
          <nav style={{...styles.sidebar, background: colors.surface, borderRight: `1px solid ${colors.border}`}}>
            {navItems.map(item => (
              <button
                type="button"
                key={item.id}
                data-tour={item.id}
                style={{
                  ...styles.navItem,
                  color: activeSection === item.id ? colors.primary : colors.textSecondary,
                  ...(activeSection === item.id ? {...styles.navItemActive, borderLeftColor: colors.primary} : {})
                }}
                onClick={() => selectSection(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>
        )}

        <main
          className={`app-scroll${isMobile ? ' mobile-under-header' : ''}${activeSection === 'assistant' ? ' assistant-pane' : ''}`}
          data-pacarion-build="header-legal-2"
          style={{
          ...styles.content,
          padding: isMobile ? '1rem' : '4rem 3rem',
          paddingTop: isMobile ? 'calc(env(safe-area-inset-top, 0px) + var(--mobile-top-bar-height, 44px) + var(--page-below-bar-gap, 16px))' : '4rem',
          paddingLeft: isMobile ? 'calc(env(safe-area-inset-left, 0px) + 16px)' : undefined,
          paddingRight: isMobile ? 'calc(env(safe-area-inset-right, 0px) + 16px)' : undefined,
          paddingBottom: isMobile ? '90px' : '4rem',
          minHeight: 'auto',
          overflowY: 'visible'
        }}>
          {activeSection === 'dashboard' && (
            <Dashboard
              profile={profile}
              stats={stats}
              onNavigate={openAgentSection}
              onStatsUpdate={loadStats}
            />
          )}
          {activeSection === 'profile' && (
            <Profile profile={profile} onProfileSaved={onProfileUpdate} />
          )}
          {activeSection === 'integrations' && isWebApp() && (
            <Integrations profile={profile} />
          )}
          {activeSection === 'clients' && (
            <ClientManagement
              profile={profile}
              onStatsUpdate={loadStats}
              onNavigate={setActiveSection}
              onOpenClient={(client) => {
                setFilteredClientId(client.id);
                setTaskStatusFilter('pending');
                setTaskViewMode('client');
                setActiveSection('tasks');
              }}
              onOpenIntegrations={isWebApp() ? () => setActiveSection('integrations') : undefined}
            />
          )}
          {activeSection === 'vendors' && (
            <VendorManagement
              profile={profile}
              onStatsUpdate={loadStats}
            />
          )}
          {activeSection === 'tasks' && (() => {
            // Filter tasks based on selected filters
            let filteredTasks = clientTasks;

            if (filteredClientId !== 'all') {
              filteredTasks = filteredTasks.filter(t => t.client_id === filteredClientId);
            }

            const helpIds = helpTaskIdSet(helpRequests);
            if (taskStatusFilter === 'attention') {
              filteredTasks = filteredTasks.filter((task) => taskNeedsAttention(task, helpIds));
            } else if (taskStatusFilter === 'pending') {
              filteredTasks = filteredTasks.filter(t => !t.completed);
            } else if (taskStatusFilter === 'completed') {
              filteredTasks = filteredTasks.filter(t => t.completed);
            }

            const brandColor = '#59c4c4';

            return (
              <div data-tour="tasks">
                {/* Task View Mode Toggle */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1.5rem',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <div style={{
                    display: 'inline-flex',
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: isMobile ? '1.25rem' : '0.5rem',
                    overflow: 'hidden'
                  }}>
                    <button
                      onClick={() => setTaskViewMode('client')}
                      style={{
                        padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 1.5rem',
                        background: taskViewMode === 'client' ? brandColor : 'transparent',
                        color: taskViewMode === 'client' ? 'white' : colors.text,
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: isMobile ? '1rem' : '0.95rem',
                        fontWeight: taskViewMode === 'client' ? '600' : 'normal',
                        minHeight: isMobile ? '44px' : '40px',
                        transition: 'all 0.2s'
                      }}
                    >
                      Client Tasks
                    </button>
                    <button
                      onClick={() => setTaskViewMode('library')}
                      style={{
                        padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 1.5rem',
                        background: taskViewMode === 'library' ? brandColor : 'transparent',
                        color: taskViewMode === 'library' ? 'white' : colors.text,
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: isMobile ? '1rem' : '0.95rem',
                        fontWeight: taskViewMode === 'library' ? '600' : 'normal',
                        minHeight: isMobile ? '44px' : '40px',
                        transition: 'all 0.2s'
                      }}
                    >
                      Task Library
                    </button>
                  </div>

                  {taskViewMode === 'client' && (
                    <button
                      onClick={() => setShowAddClientTaskModal(true)}
                      style={{
                        padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 1.5rem',
                        background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: isMobile ? '1.25rem' : '0.5rem',
                        cursor: 'pointer',
                        fontSize: isMobile ? '1rem' : '0.95rem',
                        fontWeight: '600',
                        minHeight: isMobile ? '44px' : '40px',
                        boxShadow: '0 2px 8px rgba(89, 196, 196, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      Add Task
                    </button>
                  )}
                </div>

                {/* Show either Client Tasks or Task Library based on mode */}
                {taskViewMode === 'library' ? (
                  <TaskLibrary profile={profile} />
                ) : (
                  <div>

                {/* Filters */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  flexWrap: 'wrap'
                }}>
                  {/* Client Filter - Placeholder, will be populated with actual clients */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      color: colors.textSecondary,
                      fontWeight: '500'
                    }}>
                      Filter by Client
                    </label>
                    <select
                      value={filteredClientId}
                      onChange={(e) => setFilteredClientId(e.target.value)}
                      style={{
                        padding: isMobile ? '0.75rem 1rem' : '0.5rem 1rem',
                        background: colors.surface,
                        border: `1px solid ${colors.border}`,
                        borderRadius: isMobile ? '1.25rem' : '0.5rem',
                        color: colors.text,
                        fontSize: '0.9rem',
                        minHeight: isMobile ? '44px' : '38px',
                        minWidth: '200px',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="all">All Clients</option>
                      {allClients.length > 0 && (
                        <>
                          <option disabled></option>
                          {allClients.map(client => (
                            <option key={client.id} value={client.id}>
                              {client.first_name} {client.last_name}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      color: colors.textSecondary,
                      fontWeight: '500'
                    }}>
                      Status
                    </label>
                    <div style={{
                      display: 'inline-flex',
                      background: colors.surface,
                      border: `1px solid ${colors.border}`,
                      borderRadius: isMobile ? '1.25rem' : '0.5rem',
                      overflow: 'hidden'
                    }}>
                      <button
                        onClick={() => setTaskStatusFilter('attention')}
                        style={{
                          padding: isMobile ? '0.75rem 1.25rem' : '0.5rem 1rem',
                          background: taskStatusFilter === 'attention' ? brandColor : 'transparent',
                          color: taskStatusFilter === 'attention' ? 'white' : colors.text,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: taskStatusFilter === 'attention' ? '600' : 'normal',
                          minHeight: isMobile ? '44px' : '38px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Needs attention
                      </button>
                      <button
                        onClick={() => setTaskStatusFilter('pending')}
                        style={{
                          padding: isMobile ? '0.75rem 1.25rem' : '0.5rem 1rem',
                          background: taskStatusFilter === 'pending' ? brandColor : 'transparent',
                          color: taskStatusFilter === 'pending' ? 'white' : colors.text,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: taskStatusFilter === 'pending' ? '600' : 'normal',
                          minHeight: isMobile ? '44px' : '38px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Pending
                      </button>
                      <button
                        onClick={() => setTaskStatusFilter('completed')}
                        style={{
                          padding: isMobile ? '0.75rem 1.25rem' : '0.5rem 1rem',
                          background: taskStatusFilter === 'completed' ? brandColor : 'transparent',
                          color: taskStatusFilter === 'completed' ? 'white' : colors.text,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: taskStatusFilter === 'completed' ? '600' : 'normal',
                          minHeight: isMobile ? '44px' : '38px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Completed
                      </button>
                      <button
                        onClick={() => setTaskStatusFilter('all')}
                        style={{
                          padding: isMobile ? '0.75rem 1.25rem' : '0.5rem 1rem',
                          background: taskStatusFilter === 'all' ? brandColor : 'transparent',
                          color: taskStatusFilter === 'all' ? 'white' : colors.text,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: taskStatusFilter === 'all' ? '600' : 'normal',
                          minHeight: isMobile ? '44px' : '38px',
                          transition: 'all 0.2s'
                        }}
                      >
                        All
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tasks List */}
                <div>
                  <p style={{
                    marginBottom: '1rem',
                    color: colors.textSecondary,
                    fontSize: '0.95rem'
                  }}>
                    Showing {Math.min(visibleTaskCount, filteredTasks.length)} of {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''}
                  </p>

                  {taskStatusFilter !== 'attention' && helpRequests.filter((request) => filteredClientId === 'all' || request.client_id === filteredClientId).length > 0 && (
                    <div style={{
                      background: colors.surface,
                      border: `1px solid ${colors.border}`,
                      borderRadius: isMobile ? '1.25rem' : '0.75rem',
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <h3 style={{ margin: '0 0 0.75rem', color: colors.text, fontSize: '1rem' }}>Help requests</h3>
                      {helpRequests
                        .filter((request) => filteredClientId === 'all' || request.client_id === filteredClientId)
                        .map((request) => (
                          <p key={request.id} style={{ margin: '0 0 0.5rem', color: colors.text }}>
                            <strong>{request.task_title || 'Task'}:</strong> {request.message}
                          </p>
                        ))}
                    </div>
                  )}

                  {filteredTasks.length === 0 ? (
                    <div style={{
                      background: colors.surface,
                      border: `1px solid ${colors.border}`,
                      borderRadius: isMobile ? '1.25rem' : '0.75rem',
                      padding: '3rem 2rem',
                      textAlign: 'center'
                    }}>
                      <p style={{
                        color: colors.textSecondary,
                        fontSize: '1rem',
                        margin: 0
                      }}>
                        {taskStatusFilter === 'attention' ? 'Nothing needs attention' : 'No tasks found'}
                      </p>
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem'
                    }}>
                      {(taskStatusFilter === 'attention'
                        ? groupTasksByClient(filteredTasks.slice(0, visibleTaskCount))
                        : [{ id: 'all', name: '', tasks: filteredTasks.slice(0, visibleTaskCount) }]
                      ).map((group) => (
                      <div key={group.id}>
                        {group.name ? (
                          <h3 style={{ margin: '0.5rem 0', color: colors.text, fontSize: '1rem', fontWeight: 650 }}>
                            {group.name}
                            <span style={{ color: colors.textSecondary, fontWeight: 500 }}> · {group.tasks.length}</span>
                          </h3>
                        ) : null}
                      {group.tasks.map(task => {
                        const isOverdue = isBeforeDateOnly(task.due_date, new Date()) && !task.completed;
                        const isExpanded = expandedClientTaskId === task.id;
                        const title = task.task?.title || task.task_name || 'Untitled task';
                        const home = [clientLabel(task), task.clients?.address].filter(Boolean).join(' · ');
                        const status = task.completed ? 'Completed' : (isOverdue ? 'Overdue' : 'Pending');
                        const statusColor = task.completed ? '#10b981' : (isOverdue ? '#ef4444' : colors.textSecondary);

                        return (
                          <div
                            key={task.id}
                            id={`client-task-${task.id}`}
                            style={{
                              background: colors.surface,
                              border: `1px solid ${task.completed ? '#10b981' : isOverdue ? '#ef4444' : colors.border}`,
                              borderRadius: isMobile ? '1.25rem' : '0.75rem',
                              padding: isMobile ? '1rem' : '1.25rem',
                              boxShadow: task.id === linkedTaskId ? '0 0 0 2px #59c4c4' : '0 1px 3px rgba(0,0,0,0.05)',
                              scrollMarginTop: '5rem',
                              marginBottom: '0.75rem',
                              minWidth: 0,
                              color: colors.text,
                            }}
                          >
                            <TapToExpand
                              expanded={isExpanded}
                              onToggle={() => setExpandedClientTaskId(isExpanded ? null : task.id)}
                              controls={`client-task-details-${task.id}`}
                              summary={(
                                <>
                                  <span style={{
                                    display: 'block',
                                    fontSize: isMobile ? '1.05rem' : '1.1rem',
                                    fontWeight: 650,
                                    lineHeight: 1.35,
                                    textDecoration: task.completed ? 'line-through' : 'none',
                                    overflowWrap: 'anywhere',
                                  }}>
                                    {title}
                                  </span>
                                  <span style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.85rem', color: brandColor, fontWeight: 650, overflowWrap: 'anywhere' }}>
                                    {home}
                                  </span>
                                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem', fontSize: '0.85rem', color: colors.textSecondary }}>
                                    <span>Due {formatDateOnly(task.due_date)}</span>
                                    <span style={{ color: statusColor, fontWeight: 700 }}>{status}</span>
                                  </span>
                                </>
                              )}
                            >
                              {(task.description || task.task?.description) && (
                                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: colors.textSecondary, lineHeight: '1.5' }}>
                                  {displayTaskDescription(task.description || task.task?.description, task.clients)}
                                </p>
                              )}
                              {task.client_notes && (
                                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: colors.text, lineHeight: '1.5' }}>
                                  <strong>Homeowner note:</strong> {task.client_notes}
                                </p>
                              )}
                              {helpRequests.filter((request) => request.client_task_id === task.id).map((request) => (
                                <p key={request.id} style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: colors.text, lineHeight: '1.5' }}>
                                  <strong>Help requested:</strong> {request.message}
                                </p>
                              ))}
                              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.85rem', color: colors.textSecondary }}>
                                {(task.category || task.task?.category) && (task.category || task.task?.category) !== 'general' && (
                                  <span style={{ background: colors.surfaceAlt, padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                                    {task.category || task.task?.category}
                                  </span>
                                )}
                                {task.completed && task.completed_at && (
                                  <span style={{ color: '#10b981', fontWeight: 650 }}>
                                    Completed {new Date(task.completed_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              {agentMayMarkTaskDone(task) && (
                                <button
                                  type="button"
                                  onClick={() => handleToggleClientTask(task.id, task.completed)}
                                  style={{
                                    marginTop: '0.9rem',
                                    padding: '0.75rem 1.25rem',
                                    background: task.completed ? '#10b981' : colors.surfaceAlt,
                                    color: task.completed ? 'white' : colors.text,
                                    border: `1px solid ${task.completed ? '#10b981' : colors.border}`,
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: 650,
                                    minHeight: '44px',
                                  }}
                                >
                                  {task.completed ? 'Done' : 'Mark Done'}
                                </button>
                              )}
                            </TapToExpand>
                          </div>
                        );
                      })}
                      </div>
                      ))}
                      {visibleTaskCount < filteredTasks.length && (
                        <button
                          type="button"
                          onClick={() => setVisibleTaskCount((count) => count + 25)}
                          style={{
                            padding: '0.85rem 1.25rem',
                            background: colors.surface,
                            color: colors.text,
                            border: `1px solid ${colors.border}`,
                            borderRadius: isMobile ? '1.25rem' : '0.5rem',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: '600',
                            minHeight: '44px'
                          }}
                        >
                          Show more ({filteredTasks.length - visibleTaskCount} remaining)
                        </button>
                      )}
                    </div>
                  )}
                </div>
                  </div>
                )}
              </div>
            );
          })()}
          {activeSection === 'projects' && (
            <ProjectManager profile={profile} />
          )}
          {activeSection === 'team' && (
            profile?.is_team_owner ? (
              <TeamManagement profile={profile} />
            ) : (
              <>
                <TeamDashboard profile={profile} />
                <TeamManagement profile={profile} />
              </>
            )
          )}
          {activeSection === 'assistant' && (
            <AssistantThread
              intro="Ask about a home you sponsor. Your chats stay here."
              messages={chatMessages}
              loading={threadLoading}
              loadingOlder={threadLoadingOlder}
              hasOlder={threadHasOlder}
              onLoadOlder={loadOlderAgentChat}
              onSearch={searchAgentChat}
              onReveal={revealAgentChat}
              onStartFresh={handleAgentStartFresh}
              onRetry={(msg) => handleSendMessage(msg)}
              onExample={(example) => setUserInput(example)}
              aiLoading={aiLoading}
              colors={colors}
              isMobile={isMobile}
              brandColor={colors.primary}
            >
                <div>
                  {/* Image Upload Preview */}
                  {selectedImage && (
                    <div style={{
                      background: colors.surfaceAlt,
                      border: `2px solid #59c4c4`,
                      borderRadius: isMobile ? '1.25rem' : '0.5rem',
                      padding: '0.75rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: imagePreviewUrl ? '0.5rem' : '0'
                      }}>
                        <span style={{fontWeight: 500, color: colors.text, fontSize: '0.9rem'}}>{selectedImage.name}</span>
                        <button
                          onClick={() => handleImageSelect(null)}
                          style={{background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: colors.text, padding: '0 0.5rem'}}
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                      {imagePreviewUrl && (
                        <img
                          src={imagePreviewUrl}
                          alt="Preview"
                          style={{
                            width: '100%',
                            maxHeight: '200px',
                            borderRadius: isMobile ? '1.25rem' : '0.5rem',
                            objectFit: 'contain',
                            display: 'block'
                          }}
                        />
                      )}
                    </div>
                  )}

                  {/* Textarea */}
                  <textarea
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type your message..."
                    style={{
                      width: '100%',
                      padding: isMobile ? '0.75rem 1rem' : '0.75rem',
                      border: `2px solid ${colors.primary}`,
                      borderRadius: isMobile ? '1.25rem' : '0.5rem',
                      fontSize: isMobile ? '1rem' : '1rem',
                      resize: 'none',
                      minHeight: isMobile ? '44px' : '60px',
                      maxHeight: isMobile ? '88px' : '120px',
                      background: isDark ? colors.surfaceAlt : '#FFFFFF',
                      color: colors.text,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      boxShadow: `0 0 0 4px ${colors.primary}20`,
                      outline: 'none',
                      marginBottom: '0.75rem',
                      boxSizing: 'border-box'
                    }}
                    disabled={aiLoading}
                  />

                  {/* Buttons */}
                  <div style={{display: 'flex', gap: '0.5rem'}}>
                    <label style={{
                      background: colors.surfaceAlt,
                      padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 1.25rem',
                      borderRadius: isMobile ? '1.25rem' : '0.5rem',
                      cursor: 'pointer',
                      border: `2px solid ${colors.borderLight}`,
                      color: colors.text,
                      fontSize: isMobile ? '1rem' : '0.9rem',
                      fontWeight: isMobile ? '600' : 'normal',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: isMobile ? '44px' : '44px'
                    }}>
                      Attach
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageSelect(e.target.files[0])}
                        style={{display: 'none'}}
                        disabled={aiLoading}
                      />
                    </label>
                    <button
                      onClick={handleSendMessage}
                      style={{
                        padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 1.5rem',
                        background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: isMobile ? '1.25rem' : '0.5rem',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: isMobile ? '1rem' : '1rem',
                        minHeight: isMobile ? '44px' : '44px',
                        minWidth: isMobile ? '80px' : 'auto',
                        boxShadow: '0 2px 8px rgba(89, 196, 196, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                      disabled={aiLoading || (!userInput.trim() && !selectedImage)}
                    >
                      Send
                    </button>
                  </div>
                  <AgentAiNotice color={colors.textSecondary} />
                  <QuestionsLeftNote
                    used={questionUsage.used}
                    limit={questionUsage.limit || monthlyQuestionLimit(process.env)}
                    color={colors.textSecondary}
                  />
                </div>
            </AssistantThread>
          )}
          {activeSection === 'home-history' && (
            <HomeHistoryReportAgent colors={colors} isMobile={isMobile} />
          )}
          {activeSection === 'settings' && (
            <Settings
              profile={profile}
              onLogout={onLogout}
              onNavigate={setActiveSection}
              onReplayTour={() => setTourReplay((count) => count + 1)}
              changePlanRequest={changePlanRequest}
              changePlanPreset={changePlanPreset}
              moreListToken={moreListToken}
            />
          )}
        </main>
      </div>

      {/* Add Client Task Modal */}
      {showAddClientTaskModal && (
        <ModalLayer className="app-modal-layer" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div className="app-modal-card" style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: isMobile ? '1.25rem' : '0.75rem',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: isMobile ? '1.5rem' : '2rem'
          }}>
            <div className="app-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', background: colors.surface }}>
              <h2 style={{
                color: colors.text,
                margin: 0,
                fontSize: isMobile ? '1.5rem' : '1.75rem'
              }}>
                Add Task for Client
              </h2>
              <button
                type="button"
                className="app-modal-close"
                aria-label="Close"
                onClick={() => {
                  setShowAddClientTaskModal(false);
                  setNewClientTaskData({
                    client_id: '',
                    title: '',
                    description: '',
                    due_date: '',
                    category: 'general'
                  });
                }}
                style={{ color: colors.text, background: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            {/* Client Select */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                color: colors.text,
                fontWeight: '500'
              }}>
                Select Client *
              </label>
              <select
                value={newClientTaskData.client_id}
                onChange={(e) => setNewClientTaskData({...newClientTaskData, client_id: e.target.value})}
                style={{
                  width: '100%',
                  padding: isMobile ? '0.75rem' : '0.625rem',
                  background: colors.surface,
                  border: `2px solid #59c4c4`,
                  borderRadius: isMobile ? '1.25rem' : '0.5rem',
                  color: colors.text,
                  fontSize: '1rem',
                  minHeight: isMobile ? '44px' : '40px'
                }}
              >
                <option value="">Choose a client...</option>
                {allClients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.first_name} {client.last_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Task Title */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                color: colors.text,
                fontWeight: '500'
              }}>
                Task Title *
              </label>
              <input
                type="text"
                value={newClientTaskData.title}
                onChange={(e) => setNewClientTaskData({...newClientTaskData, title: e.target.value})}
                style={{
                  width: '100%',
                  padding: isMobile ? '0.75rem' : '0.625rem',
                  background: colors.surface,
                  border: `2px solid #59c4c4`,
                  borderRadius: isMobile ? '1.25rem' : '0.5rem',
                  color: colors.text,
                  fontSize: '1rem',
                  minHeight: isMobile ? '44px' : '40px'
                }}
                placeholder="e.g., Change HVAC filter"
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                color: colors.text,
                fontWeight: '500'
              }}>
                Description (optional)
              </label>
              <textarea
                value={newClientTaskData.description}
                onChange={(e) => setNewClientTaskData({...newClientTaskData, description: e.target.value})}
                style={{
                  width: '100%',
                  padding: isMobile ? '0.75rem' : '0.625rem',
                  background: colors.surface,
                  border: `2px solid #59c4c4`,
                  borderRadius: isMobile ? '1.25rem' : '0.5rem',
                  color: colors.text,
                  fontSize: '1rem',
                  minHeight: '80px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                placeholder="Add details about this task..."
              />
            </div>

            {/* Due Date */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                color: colors.text,
                fontWeight: '500'
              }}>
                Due Date *
              </label>
              <input
                type="date"
                value={newClientTaskData.due_date}
                onChange={(e) => setNewClientTaskData({...newClientTaskData, due_date: e.target.value})}
                style={{
                  width: '100%',
                  padding: isMobile ? '0.75rem' : '0.625rem',
                  background: colors.surface,
                  border: `2px solid #59c4c4`,
                  borderRadius: isMobile ? '1.25rem' : '0.5rem',
                  color: colors.text,
                  fontSize: '1rem',
                  minHeight: isMobile ? '44px' : '40px'
                }}
                min={localDateString(new Date())}
              />
            </div>

            {/* Category */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                color: colors.text,
                fontWeight: '500'
              }}>
                Category
              </label>
              <select
                value={newClientTaskData.category}
                onChange={(e) => setNewClientTaskData({...newClientTaskData, category: e.target.value})}
                style={{
                  width: '100%',
                  padding: isMobile ? '0.75rem' : '0.625rem',
                  background: colors.surface,
                  border: `2px solid #59c4c4`,
                  borderRadius: isMobile ? '1.25rem' : '0.5rem',
                  color: colors.text,
                  fontSize: '1rem',
                  minHeight: isMobile ? '44px' : '40px'
                }}
              >
                <option value="general">General</option>
                <option value="hvac">HVAC</option>
                <option value="plumbing">Plumbing</option>
                <option value="electrical">Electrical</option>
                <option value="roof">Roof</option>
                <option value="pool">Pool</option>
                <option value="landscaping">Landscaping</option>
                <option value="appliances">Appliances</option>
                <option value="seasonal">Seasonal</option>
              </select>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleAddClientTask}
                style={{
                  flex: 1,
                  padding: isMobile ? '0.75rem 1.5rem' : '0.625rem 1.5rem',
                  background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: isMobile ? '1.25rem' : '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  minHeight: isMobile ? '44px' : '40px',
                  boxShadow: '0 2px 8px rgba(89, 196, 196, 0.3)'
                }}
              >
                Add Task
              </button>
              <button
                onClick={() => {
                  setShowAddClientTaskModal(false);
                  setNewClientTaskData({
                    client_id: '',
                    title: '',
                    description: '',
                    due_date: '',
                    category: 'general'
                  });
                }}
                style={{
                  flex: 1,
                  padding: isMobile ? '0.75rem 1.5rem' : '0.625rem 1.5rem',
                  background: colors.surfaceAlt,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: isMobile ? '1.25rem' : '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  minHeight: isMobile ? '44px' : '40px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalLayer>
      )}

    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    marginBottom: '2.5rem',
    fontWeight: 300,
    letterSpacing: '0.05em'
  },
  propertyCard: {
    padding: '2rem',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  taskCard: {
    padding: '2.5rem',
    borderRadius: '0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    transition: 'all 0.2s ease'
  },
  header: {
    padding: '2rem 3rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    flexWrap: 'wrap',
    gap: '1rem'
  },
  headerTitle: {
    margin: 0,
    fontSize: '1.75rem',
    fontWeight: 300,
    letterSpacing: '0.05em'
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  agentName: {
    fontSize: '0.9rem',
    fontWeight: 300,
    letterSpacing: '0.03em'
  },
  themeToggleBtn: {
    background: 'transparent',
    border: '1px solid',
    padding: '0.6rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 400,
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
    letterSpacing: '0.02em'
  },
  logoutBtn: {
    background: 'transparent',
    border: '1px solid',
    padding: '0.6rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 400,
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
    letterSpacing: '0.02em'
  },
  main: {
    display: 'flex',
    minHeight: 'calc(100vh - 80px)',
    flexDirection: window.innerWidth < 768 ? 'column' : 'row'
  },
  sidebar: {
    width: window.innerWidth < 768 ? '100%' : '250px',
    padding: window.innerWidth < 768 ? '0' : '2rem 0',
    display: window.innerWidth < 768 ? 'flex' : 'block',
    overflowX: window.innerWidth < 768 ? 'auto' : 'visible',
    gap: window.innerWidth < 768 ? '0' : '0',
    position: 'sticky',
    top: 0
  },
  navItem: {
    width: '100%',
    textAlign: 'left',
    fontFamily: 'inherit',
    background: 'transparent',
    padding: window.innerWidth < 768 ? '1rem 1.5rem' : '1.2rem 2rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderTop: 'none',
    borderRight: 'none',
    borderLeft: window.innerWidth < 768 ? 'none' : '2px solid transparent',
    borderBottom: window.innerWidth < 768 ? '2px solid transparent' : 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    fontSize: '0.9rem',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
    minWidth: window.innerWidth < 768 ? 'auto' : 'unset'
  },
  navItemActive: {
    background: 'transparent',
    fontWeight: 400
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: '4rem 3rem',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%'
  }
};

export default AdminDashboard;
